import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useDateRange } from "@/lib/dateRange";
import { channelParam, useChannel } from "@/lib/channel";
import { useCurrency, fromUSD, toUSD } from "@/lib/currency";
import PageHeader from "@/components/PageHeader";
import Card from "@/components/Card";
import KpiCard from "@/components/KpiCard";
import MobileKpiRail from "@/components/MobileKpiRail";
import AssigneeCombobox from "@/components/AssigneeCombobox";
import CurrencySelect from "@/components/CurrencySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, fmtDateShort } from "@/lib/format";
import { Plus, Trash2, Truck, Sparkles, User, DollarSign, Wand2, Pencil, TrendingDown, Package, Calendar, Tag } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
  BarChart, Bar, Cell,
} from "recharts";

const CATEGORIES = ["Rent", "Salaries", "Marketing", "Software", "Utilities", "Shipping Supplies", "Travel", "General"];
const ASSIGNEE_TYPES = [
  { key: "none", label: "Unassigned" },
  { key: "vendor", label: "Vendor", icon: Truck, color: "#B77B68" },
  { key: "agency", label: "Agency", icon: Sparkles, color: "#A18E9F" },
  { key: "staff", label: "Staff", icon: User, color: "#7A9BA0" },
  { key: "payroll", label: "Payroll", icon: DollarSign, color: "#7D9B7A" },
];

const CAT_COLORS = ["#B75B50", "#C8915A", "#C5A56A", "#7D9B7A", "#7A9BA0", "#A18E9F", "#B77B68", "#81766D"];

const EMPTY_FORM = {
  description: "", category: "General", amount: 0, currency: "IDR",
  assignee_type: "none", assignee_id: "", assignee_name: "",
  date: new Date().toISOString().slice(0, 10),
};

function AssigneeBadge({ type, name }) {
  const meta = ASSIGNEE_TYPES.find((t) => t.key === type);
  if (!meta || meta.key === "none" || !name) return <span className="text-[11.5px] text-[#8E8E93]">—</span>;
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: `${meta.color}15`, color: meta.color }}>
      {Icon && <Icon className="w-3 h-3" />}{name}
    </span>
  );
}

function ChartTooltip({ active, payload, label }) {
  const { fmtMoney } = useCurrency();
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.10)] border border-black/[0.06] px-3 py-2.5 text-[12px]">
      {label && <div className="text-[#8E8E93] mb-1.5 text-[11px]">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-[#3C3C43]/80 font-medium">{p.name}:</span>
          <span className="font-mono text-[#1C1C1E] font-semibold">{fmtMoney(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Expenses() {
  const { range } = useDateRange();
  const { channel } = useChannel();
  const { fmtMoney, displayCurrency, CURRENCIES } = useCurrency();
  const [expenses, setExpenses] = useState([]);
  const [expSeries, setExpSeries] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [directory, setDirectory] = useState({ vendor: [], agency: [], staff: [] });
  const [suggestion, setSuggestion] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const fmtAxis = (v) => {
    const cur = CURRENCIES.find((c) => c.code === displayCurrency) || CURRENCIES[0];
    const converted = v * cur.rate;
    const sym = cur.symbol;
    if (Math.abs(converted) >= 1_000_000) return `${sym}${(converted / 1_000_000).toFixed(1)}M`;
    if (Math.abs(converted) >= 1_000) return `${sym}${(converted / 1_000).toFixed(0)}k`;
    return `${sym}${converted.toFixed(0)}`;
  };

  const load = () =>
    Promise.all([
      api.get("/expenses", { params: { start: range.start, end: range.end, channel: channelParam(channel) } }),
      api.get("/analytics/expense-series", { params: { start: range.start, end: range.end, channel: channelParam(channel) } }),
      api.get("/products"),
    ]).then(([e, s, p]) => {
      setExpenses(e.data || []);
      setExpSeries(s.data?.series || []);
      setProducts(p.data || []);
    });

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [range.start, range.end, channel]);

  useEffect(() => {
    if (!open) { setForm(EMPTY_FORM); setSuggestion(null); setEditItem(null); return; }
    Promise.all([api.get("/vendors"), api.get("/agencies"), api.get("/staff")])
      .then(([v, a, s]) => setDirectory({ vendor: v.data || [], agency: a.data || [], staff: s.data || [] }));
  }, [open]);

  // Auto-suggest assignee from description text
  useEffect(() => {
    if (!form.description || form.description.length < 3 || form.assignee_type !== "none") { setSuggestion(null); return; }
    const words = form.description.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length >= 3);
    if (!words.length) return setSuggestion(null);
    let best = null;
    for (const type of ["vendor", "agency", "staff"]) {
      for (const item of directory[type]) {
        const name = (item.name || "").toLowerCase();
        const nameWords = name.split(/[^a-z0-9]+/).filter(Boolean);
        let score = 0;
        for (const w of words) if (name.includes(w)) score += 2;
        for (const nw of nameWords) if (nw.length >= 3 && form.description.toLowerCase().includes(nw)) score += 3;
        if (score > 0 && (!best || score > best.score)) best = { type, id: item.id, name: item.name, score };
      }
    }
    setSuggestion(best && best.score >= 3 ? best : null);
  }, [form.description, form.assignee_type, directory]);

  const openCreate = () => { setEditItem(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (e) => {
    setEditItem(e);
    setForm({
       description: e.description, category: e.category || "General", amount: fromUSD(e.amount, "IDR"),
       currency: "IDR",
      assignee_type: e.assignee_type || "none", assignee_id: e.assignee_id || "", assignee_name: e.assignee_name || "",
      date: e.date ? e.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.description || !form.amount) return toast.error("Description and amount required");
    const amountUSD = toUSD(+form.amount, form.currency);
    const payload = {
      description: form.description, category: form.category, amount: amountUSD,
      date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
      assignee_type: form.assignee_type === "none" ? null : form.assignee_type,
      assignee_id: form.assignee_type === "none" ? null : form.assignee_id || null,
      assignee_name: form.assignee_type === "none" ? null : form.assignee_name || null,
    };
    try {
      if (editItem) {
        await api.put(`/expenses/${editItem.id}`, { ...editItem, ...payload });
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense added");
      }
      setOpen(false);
      load();
    } catch { toast.error("Failed to save expense"); }
  };

  const remove = async (id) => {
    await api.delete(`/expenses/${id}`);
    toast.success("Expense deleted");
    load();
  };

  // Derived analytics
  const total = expenses.reduce((s, e) => s + e.amount, 0);
  const byCat = expenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount;
    return acc;
  }, {});
  const catRows = Object.entries(byCat)
    .sort((a, b) => b[1] - a[1])
    .map(([category, amount], i) => ({ category, amount, color: CAT_COLORS[i % CAT_COLORS.length] }));
  const topCat = catRows[0];

  const rangeStart = new Date(range.start);
  const rangeEnd = new Date(range.end);
  const rangeDays = Math.max(1, Math.round((rangeEnd - rangeStart) / 86_400_000));
  const dailyAvg = total / rangeDays;

  const inventoryCost = products.reduce((s, p) => s + (p.stock || 0) * (p.cost || 0), 0);
  const hasExpenseSeriesData = expSeries.some((point) => Number(point.amount) !== 0);

  const needsAssignee = form.assignee_type && form.assignee_type !== "none";
  const isConverting = false;
  const maxCat = catRows.length > 0 ? catRows[0].amount : 1;

  return (
    <div data-testid="expenses-page" className="max-w-[1400px] mx-auto">
      <PageHeader
        title="Expenses"
        subtitle={`${expenses.length} entries · ${fmtMoney(total)} in period`}
        action={
          <Button data-testid="add-expense-btn" onClick={openCreate} className="rounded-full bg-black text-white hover:bg-black/85">
            <Plus className="w-4 h-4 mr-1.5" /> Add expense
          </Button>
        }
      />

      {/* ── DASHBOARD ─────────────────────────────────── */}

      {/* KPI cards */}
      <MobileKpiRail className="mb-4 sm:mb-6">
        <KpiCard
          testid="exp-kpi-total"
          label="Total expenses"
           value={expenses.length ? fmtMoney(total) : "—"}
          sub={`${expenses.length} entries this period`}
          icon={TrendingDown}
          color="#B75B50"
          sparkline={expSeries}
          sparkKey="amount"
        />
        <KpiCard
          testid="exp-kpi-top-cat"
          label="Largest category"
          value={topCat ? topCat.category : "—"}
          sub={topCat ? fmtMoney(topCat.amount) : "No expenses yet"}
          icon={Tag}
          color="#C8915A"
        />
        <KpiCard
          testid="exp-kpi-daily"
          label="Daily average"
           value={expenses.length ? fmtMoney(dailyAvg) : "—"}
           sub={expenses.length ? `Over ${rangeDays} days` : "No expenses yet"}
          icon={Calendar}
          color="#7A9BA0"
          sparkline={expSeries}
          sparkKey="amount"
        />
        <KpiCard
          testid="exp-kpi-inventory"
          label="Inventory cost"
          value={fmtMoney(inventoryCost)}
          sub="Stock on hand × unit cost"
          icon={Package}
          color="#7D9B7A"
        />
      </MobileKpiRail>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Expense trend */}
        <Card
          testid="exp-trend-card"
          className="lg:col-span-2"
          title="Expense trend"
          subtitle="Total spend over the selected period"
        >
          <div className="chart-container mobile-chart h-[240px] sm:h-[280px] -ml-2">
            {hasExpenseSeriesData ? (
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 240 }}>
                <AreaChart data={expSeries} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="exp-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF3B30" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#FF3B30" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(0,0,0,0.04)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={fmtDateShort}
                    tick={{ fill: "#8E8E93", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "#8E8E93", fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={fmtAxis}
                    width={52}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="amount"
                    name="Expenses"
                    stroke="#FF3B30"
                    strokeWidth={2.4}
                    fill="url(#exp-grad)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-[13px] text-[#8E8E93]">No expense data in this period</div>
            )}
          </div>
        </Card>

        {/* Category breakdown */}
        <Card testid="exp-cat-card" title="By category" subtitle="Spend breakdown">
          <div className="space-y-3">
            {catRows.length === 0 && (
              <div className="py-8 text-center text-[13px] text-[#8E8E93]">No expenses yet</div>
            )}
            {catRows.slice(0, 7).map((row) => {
              const pct = maxCat > 0 ? Math.min(100, (row.amount / maxCat) * 100) : 0;
              return (
                <div key={row.category}>
                  <div className="flex items-center justify-between text-[12.5px] mb-1">
                    <span className="flex items-center gap-1.5 font-medium text-[#1C1C1E]">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: row.color }} />
                      {row.category}
                    </span>
                    <span className="font-mono font-semibold text-[#1C1C1E]">{fmtMoney(row.amount)}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#F5F5F7] overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: row.color }} />
                  </div>
                </div>
              );
            })}

            {/* Inventory cost line */}
            {inventoryCost > 0 && (
              <div className="pt-3 mt-1 border-t border-black/[0.05]">
                <div className="flex items-center justify-between text-[12.5px]">
                  <span className="flex items-center gap-1.5 text-[#3C3C43]/70">
                    <Package className="w-3.5 h-3.5 text-[#34C759]" />
                    Inventory on hand
                  </span>
                  <span className="font-mono font-semibold text-[#34C759]">{fmtMoney(inventoryCost)}</span>
                </div>
                <div className="text-[11px] text-[#8E8E93] mt-0.5 ml-5">Stock × unit cost (not an expense)</div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* ── FORM DIALOG ───────────────────────────────── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="mobile-dialog rounded-3xl sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle className="font-display">{editItem ? "Edit expense" : "New expense"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Description</Label>
              <Input data-testid="expense-input-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Q1 ad spend" className="rounded-xl" />
              {suggestion && !editItem && (
                <button type="button" data-testid="assignee-suggestion" onClick={() => { setForm({ ...form, assignee_type: suggestion.type, assignee_id: suggestion.id, assignee_name: suggestion.name }); setSuggestion(null); }}
                  className="mt-1 inline-flex items-center gap-2 text-[11.5px] text-[#A9684E] bg-[#A9684E]/10 border border-[#A9684E]/20 rounded-full px-2.5 py-1 self-start hover:bg-[#A9684E]/15 transition-colors">
                  <Wand2 className="w-3 h-3" />Assign to {suggestion.type} <span className="font-semibold">{suggestion.name}</span>?
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label className="text-[12px]">Amount</Label>
                <div className="flex items-center gap-1.5">
                  <Input
                    data-testid="expense-input-amount"
                    type="number" step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="rounded-xl font-mono flex-1 min-w-0"
                  />
                  <CurrencySelect value={form.currency} onChange={(c) => setForm({ ...form, currency: c })} />
                </div>
                {isConverting && form.amount > 0 && (
                  <div className="text-[10.5px] text-[#8E8E93]">
                    ≈ {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(toUSD(+form.amount, form.currency))} USD saved
                  </div>
                )}
              </div>
              <div className="grid gap-1.5">
                <Label className="text-[12px]">Date</Label>
                <Input data-testid="expense-input-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl font-mono" />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label className="text-[12px]">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-input-category" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="grid gap-1.5 pt-2 border-t border-black/[0.05]">
              <Label className="text-[12px]">Assign to</Label>
              <div className="flex flex-wrap gap-1.5">
                {ASSIGNEE_TYPES.map((t) => {
                  const active = form.assignee_type === t.key;
                  const Icon = t.icon;
                  return (
                    <button key={t.key} type="button" data-testid={`assignee-type-${t.key}`}
                      onClick={() => setForm({ ...form, assignee_type: t.key, assignee_id: "", assignee_name: "" })}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-all border ${active ? "bg-[#1C1C1E] text-white border-[#1C1C1E]" : "bg-white text-[#3C3C43]/80 border-black/10 hover:border-black/25"}`}
                      style={active && t.color ? { background: t.color, borderColor: t.color } : undefined}>
                      {Icon && <Icon className="w-3.5 h-3.5" />}{t.label}
                    </button>
                  );
                })}
              </div>
              {needsAssignee && (
                <div className="mt-2">
                  <AssigneeCombobox type={form.assignee_type} value={form.assignee_id} onChange={(id, name) => setForm({ ...form, assignee_id: id, assignee_name: name })} testid="expense-assignee" />
                  <div className="text-[11px] text-[#8E8E93] mt-1.5">Type to search existing {form.assignee_type === "payroll" ? "payroll entries" : form.assignee_type + "s"}.</div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-expense-btn" onClick={submit} className="rounded-full bg-black text-white hover:bg-black/85">
              {editItem ? "Save changes" : "Save expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── EXPENSE TABLE ─────────────────────────────── */}
      <Card>
        <div className="data-table-scroll overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6">
          <table className="w-full text-[13px] min-w-[720px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#8E8E93]">
                <th className="py-3 px-3 font-medium">Description</th>
                <th className="py-3 px-3 font-medium">Category</th>
                <th className="py-3 px-3 font-medium">Assigned to</th>
                <th className="py-3 px-3 font-medium">Date</th>
                <th className="py-3 px-3 font-medium text-right">Amount ({displayCurrency})</th>
                <th className="py-3 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-[#8E8E93]">No expenses in range.</td></tr>}
              {expenses.map((e) => (
                <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-t border-black/[0.04] hover:bg-black/[0.015]">
                  <td className="py-3 px-3">{e.description}</td>
                  <td className="py-3 px-3"><span className="px-2 py-0.5 rounded-full bg-black/5 text-[11px]">{e.category}</span></td>
                  <td className="py-3 px-3"><AssigneeBadge type={e.assignee_type} name={e.assignee_name} /></td>
                  <td className="py-3 px-3 text-[#8E8E93]">{fmtDate(e.date)}</td>
                  <td className="py-3 px-3 text-right font-mono font-semibold text-[#FF3B30]">-{fmtMoney(e.amount)}</td>
                  <td className="py-3 px-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(e)} className="text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setConfirmId(e.id)} className="text-[#8E8E93] hover:text-[#FF3B30] transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete expense?</AlertDialogTitle>
            <AlertDialogDescription>This expense will be permanently removed and cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white"
              onClick={() => { remove(confirmId); setConfirmId(null); }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
